const VIEWPORT_MARGIN = 8;
const TOOLTIP_GAP = 4;
const ARROW_SIZE = 5;

let tooltipEl = null;
let measureEl = null;
let activeTrigger = null;

function ensureTooltip() {
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "dynamic-data-tooltip";
    tooltipEl.setAttribute("role", "tooltip");
    tooltipEl.hidden = true;
    document.body.appendChild(tooltipEl);
  }
  return tooltipEl;
}

function measureTooltip(text) {
  if (!measureEl) {
    measureEl = document.createElement("div");
    measureEl.className = "dynamic-data-tooltip-measure";
    document.body.appendChild(measureEl);
  }
  measureEl.textContent = text;
  const rect = measureEl.getBoundingClientRect();
  return { width: rect.width, height: rect.height };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function resolvePlacement(trigger, location, size) {
  const rect = trigger.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const { width: tw, height: th } = size;

  let placement = location || "top";
  let top = 0;
  let left = 0;

  const setTopPlacement = () => {
    top = rect.top - th - TOOLTIP_GAP - ARROW_SIZE;
    left = rect.left + rect.width / 2 - tw / 2;
  };

  const setBottomPlacement = () => {
    top = rect.bottom + TOOLTIP_GAP + ARROW_SIZE;
    left = rect.left + rect.width / 2 - tw / 2;
  };

  const setLeftPlacement = () => {
    top = rect.top + rect.height / 2 - th / 2;
    left = rect.left - tw - TOOLTIP_GAP - ARROW_SIZE;
  };

  const setRightPlacement = () => {
    top = rect.top + rect.height / 2 - th / 2;
    left = rect.right + TOOLTIP_GAP + ARROW_SIZE;
  };

  if (placement === "bottom") setBottomPlacement();
  else if (placement === "left") setLeftPlacement();
  else if (placement === "right") setRightPlacement();
  else {
    setTopPlacement();
    if (top < VIEWPORT_MARGIN) {
      placement = "bottom";
      setBottomPlacement();
    }
  }

  if (placement === "top" || placement === "bottom") {
    left = clamp(left, VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - tw);
    if (placement === "top") {
      top = clamp(top, VIEWPORT_MARGIN, vh - VIEWPORT_MARGIN - th);
    } else {
      top = clamp(top, VIEWPORT_MARGIN, vh - VIEWPORT_MARGIN - th);
    }
  } else {
    top = clamp(top, VIEWPORT_MARGIN, vh - VIEWPORT_MARGIN - th);
    if (placement === "left") {
      if (left < VIEWPORT_MARGIN) {
        placement = "right";
        setRightPlacement();
        left = clamp(left, VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - tw);
      }
    } else if (left + tw > vw - VIEWPORT_MARGIN) {
      placement = "left";
      setLeftPlacement();
      left = clamp(left, VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - tw);
    }
  }

  return { top, left, placement };
}

function showTooltip(trigger) {
  if (!trigger || !trigger.isConnected || !document.contains(trigger)) {
    hideTooltip();
    return;
  }
  const text = trigger.getAttribute("data-tooltip");
  if (!text) return;

  const location = trigger.getAttribute("data-tooltip-location");
  const size = measureTooltip(text);
  const { top, left, placement } = resolvePlacement(trigger, location, size);
  const el = ensureTooltip();

  el.textContent = text;
  el.hidden = false;
  el.style.top = `${top}px`;
  el.style.left = `${left}px`;
  el.dataset.placement = placement;

  if (activeTrigger && activeTrigger !== trigger) {
    activeTrigger.classList.remove("dynamic-tooltip-active");
  }

  trigger.classList.add("dynamic-tooltip-active");
  activeTrigger = trigger;
}

export function hideTooltip(trigger) {
  if (tooltipEl) tooltipEl.hidden = true;
  if (trigger) trigger.classList.remove("dynamic-tooltip-active");
  if (activeTrigger) {
    activeTrigger.classList.remove("dynamic-tooltip-active");
    activeTrigger = null;
  }
}

function handleMouseOver(event) {
  const trigger = event.target?.closest?.("[data-tooltip]");
  if (!trigger) return;
  if (activeTrigger === trigger) return;
  if (activeTrigger) hideTooltip(activeTrigger);
  showTooltip(trigger);
}

function handleMouseOut(event) {
  const trigger = event.target?.closest?.("[data-tooltip]");
  if (!trigger || activeTrigger !== trigger) return;

  const relatedTarget = event.relatedTarget;
  if (relatedTarget && trigger.contains(relatedTarget)) return;

  hideTooltip(trigger);
}

function handleClick() {
  hideTooltip();
}

function handlePointerDown(event) {
  const trigger = event.target?.closest?.("[data-tooltip]");
  if (trigger || activeTrigger) {
    hideTooltip();
  }
}

function handleReposition() {
  if (activeTrigger) {
    if (!activeTrigger.isConnected || !document.contains(activeTrigger)) {
      hideTooltip();
      return;
    }
    showTooltip(activeTrigger);
  }
}

function handleHeaderTitleMouseOver(event) {
  const headerCol = event.target?.closest?.(
    ".rdt_TableCol, .rdt_TableHeadRow [role='columnheader']"
  );
  if (!headerCol) return;

  const target =
    headerCol.querySelector(".rdt_TableCol_Sortable > div, .rdt_TableCol > div") ||
    headerCol;
  const text = (target.textContent || headerCol.textContent || "").trim();
  if (!text) return;

  const isOverflowing =
    target.scrollWidth > target.clientWidth ||
    headerCol.scrollWidth > headerCol.clientWidth;

  if (isOverflowing) {
    if (!headerCol.title) headerCol.title = text;
    if (!target.title) target.title = text;
  }
}

export function initDynamicTooltips() {
  if (globalThis.__dynamicTooltipsInit) return;
  globalThis.__dynamicTooltipsInit = true;

  document.addEventListener("mouseover", handleMouseOver);
  document.addEventListener("mouseover", handleHeaderTitleMouseOver, {
    passive: true,
  });
  document.addEventListener("mouseout", handleMouseOut);
  document.addEventListener("click", handleClick, true);
  document.addEventListener("pointerdown", handlePointerDown, true);
  window.addEventListener("resize", handleReposition);
  window.addEventListener("scroll", handleReposition, true);
  window.addEventListener("blur", () => hideTooltip());
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") hideTooltip();
  });

  if (typeof window !== "undefined" && window.history) {
    const originalPushState = window.history.pushState;
    if (originalPushState) {
      window.history.pushState = function (...args) {
        hideTooltip();
        return originalPushState.apply(this, args);
      };
    }

    const originalReplaceState = window.history.replaceState;
    if (originalReplaceState) {
      window.history.replaceState = function (...args) {
        hideTooltip();
        return originalReplaceState.apply(this, args);
      };
    }

    window.addEventListener("popstate", () => hideTooltip());
    window.addEventListener("hashchange", () => hideTooltip());
  }

  if (typeof MutationObserver !== "undefined" && typeof document !== "undefined" && document.body) {
    const observer = new MutationObserver(() => {
      if (activeTrigger && (!activeTrigger.isConnected || !document.contains(activeTrigger))) {
        hideTooltip();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}
